import { ComponentType, FC, SVGProps } from 'react';

import clsx from 'clsx';
import { Dayjs } from 'dayjs';

import BronzeBorder from '@assets/presentation-area-sponsors/bronze-border.svg?react';
import CopyIcon from '@assets/presentation-area-sponsors/copy-icon.svg?react';
import GoldBorder from '@assets/presentation-area-sponsors/gold-border.svg?react';
import CustomPromoIcon from '@assets/presentation-area-sponsors/image-dollar.webp';
import NewBadge from '@assets/presentation-area-sponsors/label-new.webp';
import SilverBorder from '@assets/presentation-area-sponsors/silver-border.svg?react';

import { LotteryPanel } from './LotteryPanel';

import css from './SponsorCard.module.css';

import { PartnerSpotlightStatusesEnum } from 'v2/constants';
import { CasinoBenefitsData, PromoPackTypeEnum } from 'v2/types/CasinoModel';
import { fileSrc } from 'v2/utils';

type SvgComponent = ComponentType<SVGProps<SVGSVGElement>>;

const TIER_FRAMES: Record<PartnerSpotlightStatusesEnum, SvgComponent> = {
  [PartnerSpotlightStatusesEnum.GOLD]: GoldBorder,
  [PartnerSpotlightStatusesEnum.SILVER]: SilverBorder,
  [PartnerSpotlightStatusesEnum.BRONZE]: BronzeBorder,
};

const TIER_CLASS: Record<PartnerSpotlightStatusesEnum, string> = {
  [PartnerSpotlightStatusesEnum.GOLD]: css.gold,
  [PartnerSpotlightStatusesEnum.SILVER]: css.silver,
  [PartnerSpotlightStatusesEnum.BRONZE]: css.bronze,
};

enum RewardSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

const REWARD_SIZE_CLASS: Record<RewardSize, string> = {
  [RewardSize.SMALL]: css.rewardSm,
  [RewardSize.MEDIUM]: css.rewardMd,
  [RewardSize.LARGE]: css.rewardLg,
};

interface PartnerInfo {
  name: string;
  logoId: string | null;
  benefits: CasinoBenefitsData;
}

export interface SponsorCardProps {
  status: PartnerSpotlightStatusesEnum;
  backgroundImageId: string;
  isBackgroundOnly: boolean;
  hasNewBadge: boolean;
  bonusReceivedText: string;
  extraOfferText: string;
  promoCode: string;
  isLotteryActive: boolean;
  lottery: {
    imageId: string;
    description: string;
    promoTexts: string[];
    endDate: Dayjs | string | null;
  };
  partnerData: PartnerInfo;
}

export const SponsorCard: FC<SponsorCardProps> = ({
  status,
  backgroundImageId,
  isBackgroundOnly,
  hasNewBadge,
  bonusReceivedText,
  extraOfferText,
  promoCode,
  isLotteryActive,
  lottery,
  partnerData,
}) => {
  const Frame = TIER_FRAMES[status];
  const { logoId, benefits, name } = partnerData;

  const isFreeSpinsAvailable = benefits.freeSpinsCount !== null && benefits.freeSpinsCount > 0;
  const isPercentsToDepositAvailable = benefits.depositPercentage !== null && benefits.depositPercentage > 0;
  const isSingleReward = !isFreeSpinsAvailable || !isPercentsToDepositAvailable;
  const benefitsCount = (isFreeSpinsAvailable ? 1 : 0) + (isPercentsToDepositAvailable ? 1 : 0);
  const isCustomPromoAvailable = benefits.promoPackType === PromoPackTypeEnum.CUSTOM;
  const trimmedPromoCode = promoCode.trim();
  const isPromoCodeVisible = !isLotteryActive && trimmedPromoCode.length > 0;

  const getRewardSize = (): RewardSize => {
    if (isLotteryActive) return RewardSize.SMALL;
    if (benefitsCount === 2) return RewardSize.MEDIUM;
    return RewardSize.LARGE;
  };

  const rewardSize = getRewardSize();

  return (
    <div className={clsx(css.slot, TIER_CLASS[status])}>
      <Frame className={css.cardFrame} aria-hidden />

      <div
        className={css.card}
        style={{ backgroundImage: backgroundImageId ? `url(${fileSrc(backgroundImageId)})` : undefined }}
      >
        {!isBackgroundOnly && (
          <>
            {hasNewBadge && <img src={NewBadge} alt="new badge" className={css.newBadge} />}

            <div className={css.content}>
              {bonusReceivedText && <p className={css.bonusReceivedLabel}>{bonusReceivedText}</p>}

              {logoId ? (
                <img className={css.sponsorLogo} src={fileSrc(logoId)} alt={name} />
              ) : (
                <div className={css.sponsorLogoFallback}>{name || 'Casino'}</div>
              )}

              <div className={css.rewardsContainer}>
                {extraOfferText && <span className={css.extraOfferChip}>{extraOfferText}</span>}

                <div className={clsx(css.bonusesContainer, REWARD_SIZE_CLASS[rewardSize])}>
                  {isPercentsToDepositAvailable && (
                    <div className={css.reward}>
                      <div className={css.rewardValue}>
                        <span>{benefits.depositPercentage}%</span>
                      </div>
                      <p className={css.rewardLabel}>To the deposit</p>
                    </div>
                  )}

                  {!isSingleReward && <div className={css.plus}>+</div>}

                  {isFreeSpinsAvailable && (
                    <div className={css.reward}>
                      <div className={css.rewardValue}>
                        <span>{benefits.freeSpinsCount}</span>
                      </div>
                      <p className={css.rewardLabel}>Free spins</p>
                    </div>
                  )}

                  {isCustomPromoAvailable && (
                    <div className={isLotteryActive ? css.customPromoRewardWithLottery : css.customPromoReward}>
                      <img className={css.customPromoIcon} src={CustomPromoIcon} alt="Custom promo icon" />

                      <div
                        className={isLotteryActive ? css.customPromoRewardValue : css.customPromoRewardValueWithLottery}
                      >
                        <span>{benefits.customPromo}</span>
                      </div>
                    </div>
                  )}
                </div>

                {isLotteryActive && <LotteryPanel {...lottery} />}

                {isPromoCodeVisible && (
                  <div className={css.promoCode}>
                    <div className={css.promoCodeContainer}>
                      <span className={css.promoCodeText}>{trimmedPromoCode}</span>
                      <CopyIcon className={css.promoCodeIcon} aria-hidden />
                    </div>
                  </div>
                )}
              </div>

              <button type="button" className={css.button} disabled>
                Забрать бонус
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
